import React from 'react'
import PropTypes from 'prop-types'
import { WorkspaceSelection } from './workspaceSelection'
import { WorkspaceUsage } from './workspaceUsage'
import { SidebarMenu } from './menu'
import { Lock, ChevronLeft, ChevronRight, Settings } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useSidebarStore } from '@/stores/sidebarStore'
import { CompanyLogomark } from '@/components/icons/CompanyLogomark'
import companyLogo from '@/assets/evermuse-logo-15h.svg'

export function Sidebar({ selectedMenu, setSelectedMenu, isOnboarding }) {
  const { isCollapsed, toggleCollapsed, setCollapsed } = useSidebarStore()
  const autoExpandedFromCollapsedRef = React.useRef(false)

  // Automatically expand when navigating into Settings from mini, and restore on exit
  React.useEffect(() => {
    const settingsMenus = ['team-settings', 'products', 'competitors', 'integrations', 'team']
    const isInSettings = settingsMenus.includes(selectedMenu)

    if (isInSettings) {
      if (isCollapsed) {
        setCollapsed(false)
        autoExpandedFromCollapsedRef.current = true
      }
    } else {
      if (autoExpandedFromCollapsedRef.current) {
        setCollapsed(true)
        autoExpandedFromCollapsedRef.current = false
      }
    }
  }, [selectedMenu, isCollapsed, setCollapsed])

  return (
    <div className={`h-screen flex flex-col ${isCollapsed ? 'w-[64px]' : 'w-[200px]'}`}>
      <div
        className={`relative flex items-center ${isCollapsed ? 'justify-center mx-auto mt-[26px] px-2' : 'justify-between ml-[24px] mt-[26px]'}`}
      >
        <button onClick={() => setSelectedMenu('home-new')} aria-label="Go to home" className="cursor-pointer">
          {!isCollapsed ? <img src={companyLogo} alt="Evermuse" width="124" height="15" /> : <CompanyLogomark />}
        </button>
      </div>

      {!['team-settings', 'products', 'competitors', 'integrations', 'team'].includes(selectedMenu) && (
        <WorkspaceSelection />
      )}
      <div className="flex-1 overflow-y-auto min-h-0">
        <SidebarMenu
          selectedMenu={selectedMenu}
          setSelectedMenu={setSelectedMenu}
          isOnboarding={isOnboarding}
          isCollapsed={isCollapsed}
        />
      </div>
      {!['team-settings', 'products', 'competitors', 'integrations', 'team'].includes(selectedMenu) && (
        <div className={`mt-auto ${isCollapsed ? 'px-2' : 'px-5'} pb-7`}>
          <div className={`flex ${isCollapsed ? 'justify-center' : 'justify-start'} mb-3`}>
            <button
              onClick={toggleCollapsed}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronLeft className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </button>
          </div>
          <WorkspaceUsage className={`w-full mb-5 ${isCollapsed ? 'px-1' : ''}`} isCollapsed={isCollapsed} />
          {!isCollapsed && <div className="h-[1px] mb-5 border-t w-full border-grey-200 dark:border-gray-700" />}
          {isOnboarding ? (
            <TooltipProvider delayDuration={0} skipDelayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="w-full">
                    <button
                      onClick={() => setSelectedMenu('team-settings')}
                      className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} w-full opacity-40 pointer-events-none`}
                      disabled={true}
                    >
                      <div className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}>
                        <Settings className="w-5 h-5 text-[#62718D] dark:text-gray-400" />
                        {!isCollapsed && (
                          <p className="text-slate dark:text-gray-400 text-base font-medium font-['Manrope'] leading-[23px] mx-4">
                            Settings
                          </p>
                        )}
                      </div>
                      {!isCollapsed && <Lock className="w-4 h-4 text-gray-400 flex-shrink-0 mr-4" />}
                    </button>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-black text-white border-black">
                  <p>Complete setup to unlock</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <TooltipProvider delayDuration={0} skipDelayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSelectedMenu('team-settings')}
                    className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} w-full`}
                  >
                    <div className={`flex items-center ${isCollapsed ? '' : 'flex-1'}`}>
                      <Settings
                        className={`w-5 h-5 ${
                          selectedMenu !== 'team-settings'
                            ? 'text-[#62718D] dark:text-gray-400'
                            : 'text-[#FF4A8E] dark:text-[#FF4A8E]'
                        }`}
                      />
                      {!isCollapsed &&
                        (selectedMenu !== 'team-settings' ? (
                          <p className="text-slate dark:text-gray-400 text-base font-medium font-['Manrope'] leading-[23px] mx-4">
                            Settings
                          </p>
                        ) : (
                          <p className="text-black dark:text-white text-base font-semibold font-['Manrope'] leading-tight mx-4">
                            Settings
                          </p>
                        ))}
                    </div>
                  </button>
                </TooltipTrigger>
                {isCollapsed && (
                  <TooltipContent side="right" className="bg-black text-white border-black">
                    <p>Settings</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
    </div>
  )
}

Sidebar.propTypes = {
  selectedMenu: PropTypes.string.isRequired,
  setSelectedMenu: PropTypes.func.isRequired,
  isOnboarding: PropTypes.bool.isRequired,
}
